import {
  styled,
  Switch,
  useTheme,
  type SwitchProps,
  switchClasses,
} from "@mui/material";

type OrionSwitchSize = "small" | "medium";

type OrionSwitchProps = Omit<SwitchProps, "size"> & {
  size?: OrionSwitchSize;
};

const sizeMap: Record<
  OrionSwitchSize,
  {
    width: number;
    height: number;
    thumbSize: number;
    border: number;
    icon: number;
  }
> = {
  medium: { width: 52, height: 28, thumbSize: 28, border: 4, icon: 20 },
  small: { width: 36, height: 20, thumbSize: 20, border: 3, icon: 14 },
};

export default function OrionSwitch({
  size = "medium",
  ...props
}: OrionSwitchProps) {
  return (
    <SwitchLovely
      ownerSize={size}
      checkedIcon={<CheckThumb size={size} disabled={props.disabled} />}
      {...props}
    />
  );
}

const CheckThumb = ({
  size,
  disabled,
}: {
  size: OrionSwitchSize;
  disabled?: boolean;
}) => {
  const theme = useTheme();
  const { thumbSize, border, icon } = sizeMap[size];
  return (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: thumbSize,
        height: thumbSize,
        background: theme.palette.common.white,
        borderRadius: "50%",
        border: `${border}px solid ${disabled ? theme.palette.action.disabled : theme.palette.primary.main}`,
      }}
    >
      <svg width={icon} height={icon} viewBox="0 0 20 20">
        <path
          fill={
            disabled
              ? theme.palette.action.disabled
              : theme.palette.primary.main
          }
          d="M7.629 14.204a1 1 0 0 1-.707-.293l-3.204-3.204a1 1 0 1 1 1.414-1.414l2.497 2.497 5.457-5.457a1 1 0 1 1 1.414 1.414l-6.164 6.164a1 1 0 0 1-.707.293z"
        />
      </svg>
    </span>
  );
};

// From https://mui-treasury.com/?path=/story/switch-lovely--lovely&globals=blockUsage:!true
export const SwitchLovely = styled(Switch, {
  shouldForwardProp: (prop) => prop !== "ownerSize",
})<{ ownerSize?: OrionSwitchSize }>(({ theme, ownerSize = "medium" }) => {
  const { width, height, thumbSize, border } = sizeMap[ownerSize];

  return {
    width: `${width}px`,
    height: `${height}px`,
    padding: 0,
    overflow: "visible",
    [`& .${switchClasses.switchBase}`]: {
      padding: 0,
      // MUI's hidden input is 300% wide (a 100%-width overhang on each side)
      // as a touch-target helper. With padding 0 that overhang is invisible
      // and swallows clicks meant for neighboring controls, so pin the hit
      // area to the visible switch in both thumb positions.
      [`& .${switchClasses.input}`]: {
        left: 0,
        width: `${width}px`,
      },
      [`&.${switchClasses.checked}`]: {
        color: theme.palette.common.white,
        transform: `translateX(${width - thumbSize}px)`,
        [`& .${switchClasses.input}`]: {
          left: `-${width - thumbSize}px`,
        },
        [`& + .${switchClasses.track}`]: {
          backgroundColor: theme.palette.primary.main,
          opacity: 1,
          border: "none",
        },
        [`&.Mui-disabled + .${switchClasses.track}`]: {
          backgroundColor: theme.palette.action.disabled,
          opacity: 0.5,
        },
      },
      [`&.Mui-disabled`]: {
        [`& + .${switchClasses.track}`]: {
          backgroundColor: theme.palette.action.disabled,
          opacity: 0.5,
        },
      },
    },
    [`& .${switchClasses.thumb}`]: {
      boxShadow: "none",
      backgroundColor: theme.palette.common.white,
      width: `${thumbSize}px`,
      height: `${thumbSize}px`,
      border: `${border}px solid ${theme.palette.text.primary}`,
    },
    [`& .Mui-disabled .${switchClasses.thumb}`]: {
      border: `${border}px solid ${theme.palette.action.disabled}`,
    },
    [`& .${switchClasses.track}`]: {
      borderRadius: height,
      backgroundColor: theme.palette.text.primary,
      opacity: 1,
      boxSizing: "border-box",
    },
  };
});
